import { useLanguage } from "../i18n/LanguageContext";
import {
  Hammer,
  Home,
  Split,
  Map,
  Leaf,
  BadgeCheck,
  KeyRound,
  Zap,
  MapPin,
  ArrowUpRight,
} from "lucide-react";
import { useReveal } from "../hooks/useReveal";

const ICONS = [Hammer, Home, Split, Map, Leaf, BadgeCheck, KeyRound, Zap, MapPin];

const ServiceCard = ({ index, Icon, title, desc }) => {
  const ref = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`service-card-${index}`}
      className="reveal group relative bg-white border border-[#E5E5E5] p-7 sm:p-8 md:p-10 transition-all duration-500 hover:border-[#1A1A1A] hover:-translate-y-1"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="flex items-start justify-between mb-8 md:mb-10">
        <div className="w-12 h-12 flex items-center justify-center border border-[#E5E5E5] text-[#52525B] transition-colors duration-500 group-hover:border-[#1A1A1A] group-hover:bg-[#1A1A1A] group-hover:text-white">
          <Icon strokeWidth={1.25} className="w-5 h-5" />
        </div>
        <span className="text-xs font-mono text-gray-400 tracking-wider">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>

      <h3 className="font-serif text-[2.1rem] sm:text-2xl md:text-3xl font-light text-[#1A1A1A] mb-3 md:mb-4 leading-tight">
        {title}
      </h3>
      <p className="text-[#52525B] text-[1.12rem] sm:text-base md:text-base leading-relaxed">{desc}</p>

      <ArrowUpRight className="absolute bottom-7 right-7 sm:bottom-8 sm:right-8 w-4 h-4 text-[#1A1A1A] opacity-0 -translate-x-2 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0" />
    </article>
  );
};

const Services = () => {
  const { t } = useLanguage();
  const headerRef = useReveal();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-white py-20 md:py-32 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div
          ref={headerRef}
          className="reveal max-w-3xl mb-12 md:mb-24"
        >
          <p className="text-[12px] sm:text-sm uppercase tracking-[0.28em] font-semibold text-[#52525B] mb-5 md:mb-6">
            — {t.services.overline}
          </p>
          <h2 className="font-serif text-[3.5rem] sm:text-5xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A1A] leading-[1.05] mb-5 md:mb-6">
            {t.services.title}
          </h2>
          <p className="text-[1.18rem] sm:text-lg md:text-lg text-[#52525B] leading-relaxed max-w-xl">
            {t.services.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {t.services.items.map((item, i) => (
            <ServiceCard
              key={item.title}
              index={i}
              Icon={ICONS[i]}
              title={item.title}
              desc={item.desc}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
